import { api } from '../utils/api';
import { API_ENDPOINTS } from '../constants/AppApiConstant';
import { API_BASE_URL } from '../constants/AppApiConstant';

/**
 * Slider Service
 * Centralized API calls for slider management
 */

// API Slider interfaces (with numeric IDs)
interface ApiSlider {
  id: number;
  photo_url: string;        // Relative path to image
  is_active: boolean;       // Whether slider is active
  display_order: number;   // Order for sorting (lower = first)
  created_at: string;      // ISO 8601 format
  updated_at: string;      // ISO 8601 format
}

// API Response format (note: different from categories/products - uses { sliders: [...] })
interface ApiSlidersResponse {
  sliders: ApiSlider[];
}

interface ApiSliderResponse {
  id: number;
  photo_url: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ApiErrorResponse {
  error: string;
}

interface ApiSuccessResponse {
  message: string;
}

// Frontend Slider interfaces (with string IDs)
export interface Slider {
  id: string;
  image: string;           // Full URL to image
  order: number;           // Display order
  isActive: boolean;       // Whether slider is active
  createdAt: string;
  updatedAt: string;
}

export interface CreateSliderRequest {
  photo: File;
  is_active?: boolean;
  display_order?: number;
}

export interface UpdateSliderRequest {
  photo_url?: string;
  is_active?: boolean;
  display_order?: number;
}

/**
 * Convert API slider (numeric IDs) to frontend slider (string IDs)
 */
const convertSlider = (apiSlider: ApiSlider): Slider => ({
  id: String(apiSlider.id),
  image: apiSlider.photo_url.startsWith('/')
    ? `${API_BASE_URL}${apiSlider.photo_url}`
    : apiSlider.photo_url,
  order: apiSlider.display_order,
  isActive: apiSlider.is_active,
  createdAt: apiSlider.created_at,
  updatedAt: apiSlider.updated_at
});

/**
 * Get all sliders
 */
export const getSliders = async (): Promise<Slider[]> => {
  try {
    const response = await api.get<ApiSlidersResponse>(API_ENDPOINTS.SLIDERS);
    
    if (!response || !response.sliders || !Array.isArray(response.sliders)) {
      console.warn('Unexpected response format from getSliders:', response);
      return [];
    }
    
    return response.sliders.map(convertSlider);
  } catch (error: any) {
    console.error('Get sliders error:', error);
    const errorMessage = error?.message || 'Failed to fetch sliders';
    throw new Error(errorMessage);
  }
};

/**
 * Get single slider by ID
 * Note: This endpoint may not be registered on the backend
 */
export const getSlider = async (id: string): Promise<Slider> => {
  try {
    const response = await api.get<ApiSliderResponse>(`${API_ENDPOINTS.SLIDERS}/${id}`);
    return convertSlider(response);
  } catch (error: any) {
    console.error('Get slider error:', error);
    const errorMessage = error?.message || 'Failed to fetch slider';
    throw new Error(errorMessage);
  }
};

/**
 * Create new slider
 */
export const createSlider = async (slider: CreateSliderRequest): Promise<Slider> => {
  try {
    const formData = new FormData();
    
    // Add photo file
    formData.append('photo', slider.photo);
    
    // Add optional fields
    if (slider.is_active !== undefined) {
      formData.append('is_active', slider.is_active ? 'true' : 'false');
    }
    
    if (slider.display_order !== undefined) {
      formData.append('display_order', slider.display_order.toString());
    }

    // Get auth token
    const token = localStorage.getItem('dawaak_token') || sessionStorage.getItem('dawaak_token');
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.SLIDERS}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type - let browser set it with boundary for multipart/form-data
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API Error: ${response.status}`);
    }

    const result = await response.json();
    return convertSlider(result);
  } catch (error: any) {
    console.error('Create slider error:', error);
    const errorMessage = error?.message || 'Failed to create slider';
    throw new Error(errorMessage);
  }
};

/**
 * Update existing slider
 */
export const updateSlider = async (id: string, slider: UpdateSliderRequest): Promise<Slider> => {
  try {
    const response = await api.put<ApiSliderResponse>(`${API_ENDPOINTS.SLIDERS}/${id}`, slider);
    return convertSlider(response);
  } catch (error: any) {
    console.error('Update slider error:', error);
    const errorMessage = error?.message || 'Failed to update slider';
    throw new Error(errorMessage);
  }
};

/**
 * Delete slider
 */
export const deleteSlider = async (id: string): Promise<void> => {
  try {
    await api.delete<ApiSuccessResponse>(`${API_ENDPOINTS.SLIDERS}/${id}`);
  } catch (error: any) {
    console.error('Delete slider error:', error);
    const errorMessage = error?.message || 'Failed to delete slider';
    throw new Error(errorMessage);
  }
};

